import { Army } from "@/domain/army";
import { UserSettings } from "@/lib/user-settings";
import { ArmyData } from "@/schema/latest";
import { useQuery } from "@/ui/database-provider";
import { KnownErrorMessage } from "@/ui/db-error";
import { useResolveVersion } from "@/ui/game-content-provider";
import { Link } from "@/ui/link";
import { LoadingIndicator } from "@/ui/loading-indicator";
import { MaybeSignupPrompt } from "@/ui/signup-prompt";
import { useUserSettingsOp } from "@/ui/user-settings/context";
import { fold, useCurrentUser, useLastSuccessfulSyncTs, useSyncState } from "@indietabletop/appkit";
import { useCallback } from "react";
import { ArmiesPageEmptyState } from "../armies-page-empty-state";
import { ListView } from "../list-view";
import { MobileNavLayout } from "../mobile-nav-layout";
import { color } from "../tokens";
import { CreateArmyButton } from "./create-army-button";
import * as css from "./style.css";

function SyncStatus(props: { updatedTs: number }) {
  const currentUser = useCurrentUser();
  const lastSyncTs = useLastSuccessfulSyncTs() ?? 0;
  const isSyncing = useSyncState() === "SYNCING";

  // If user is not logged in, don't show sync status
  if (!currentUser) {
    return null;
  }

  // User is logged in, but sync was completed after update of the entity.
  // Nothing to show.
  if (lastSyncTs > props.updatedTs) {
    return null;
  }

  return isSyncing ? " (Syncing...)" : " (Not synced)";
}

function ArmyListItem(props: { armyData: ArmyData; userSettings: UserSettings }) {
  const { armyData, userSettings } = props;
  const versionOp = useResolveVersion(armyData.ruleset_version);
  const armyStatsOp = versionOp.mapSuccess((ruleset) => new Army(ruleset, armyData));

  return (
    <Link className={css.army} href={`/army/a/${armyData.id}`}>
      <h2 className={css.armyHeading}>{Army.getNameWithFallback(armyData)}</h2>
      <p className={css.armyStats}>
        {armyStatsOp.unpack(
          (army) => {
            const usesDefaultRuleset = army.ruleset.version === userSettings.ruleset.version;

            return (
              <>
                {!army.isValid() && (
                  <>
                    <span style={{ color: color.PURPLE }}>Invalid</span>
                    {" · "}
                  </>
                )}
                {army.summary}
                {!usesDefaultRuleset && ` · Ruleset: ${army.ruleset.versionName}`}

                <SyncStatus updatedTs={army.updatedTs} />
              </>
            );
          },
          () => {
            return (
              <span style={{ color: color.PURPLE }}>
                Failed to fetch army ruleset (v{armyData.ruleset_version})
              </span>
            );
          },
          () => "Loading army ruleset...",
        )}
      </p>
    </Link>
  );
}

function ArmiesPageSwitch() {
  const armiesQuery = useQuery(
    useCallback((db) => db.getArmies(), []),
    "armies",
  );
  const userSettingsOp = useUserSettingsOp();
  const op = fold([armiesQuery, userSettingsOp]);

  if (op.isPending) {
    return <LoadingIndicator />;
  }

  if (op.isFailure) {
    return <KnownErrorMessage error={op.failure} />;
  }

  const [armiesData, userSettings] = op.value;

  if (armiesData.length === 0) {
    return <ArmiesPageEmptyState rulesetVersion={userSettings.ruleset.version} />;
  }

  const items = [
    <MaybeSignupPrompt key="prompt" armiesCount={armiesData.length} />,

    ...armiesData.map((armyData) => {
      return <ArmyListItem key={armyData.id} armyData={armyData} userSettings={userSettings} />;
    }),
  ];

  return (
    <ListView
      className={css.armies}
      items={items}
      isGrid
      stickyButton={<CreateArmyButton rulesetVersion={userSettings.ruleset.version} />}
    />
  );
}

export function ArmiesPage() {
  return (
    <MobileNavLayout title="Army Builder">
      <ArmiesPageSwitch />
    </MobileNavLayout>
  );
}
